/**
 * utils.js - AI 일정 처리 유틸리티
 * 날짜 함수 → dateUtils.js, TTS → speechUtils.js 로 분리됨 (L-4a)
 */

import {
   translateEnglishDays,
   formatDate,
   addDays,
   addWeeks,
   startOfWeek,
   endOfWeek,
   startOfMonth,
   endOfMonth,
   getWeekday,
} from './dateUtils';
// 기존 import 경로 호환성 유지 (from '../utils' 로 임포트하는 파일들)
export { translateEnglishDays };

/**
 * generateAIPrompt
 * @description 챗봇 입력을 기반으로 AI 프롬프트를 생성합니다.
 */
export const generateAIPrompt = (command, context = {}) => {
   const now = new Date();

   let contextInfo = '';
   if (context.context) {
      switch (context.context) {
         case 'profile':
            contextInfo = '현재 위치: 내 프로필 탭 - 로컬 일정 관리';
            break;
         case 'events':
            contextInfo = '현재 위치: 나의 일정 탭 - 로컬 일정 관리';
            break;
         case 'googleCalendar':
            contextInfo = '현재 위치: Google 캘린더 탭 - Google 캘린더 연동';
            break;
         default:
            contextInfo = '현재 위치: 일반 탭';
      }
   }

   return [
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `🧠 **당신은 매우 똑똑한 일정 관리 AI입니다**`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      ``,
      `사용자 입력: "${command}"`,
      `오늘 = ${formatDate(now, 'YYYY-MM-DD dddd')} (${formatDate(now, 'MM월 DD일')})`,
      `현재 시간 = ${formatDate(now, 'YYYY-MM-DD HH:mm:ss')}`,
      contextInfo ? `${contextInfo}` : '',
      ``,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `⚠️ **첫 번째: 일정 관련인지 먼저 판단하세요!**`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      ``,
      `**일정 관련 키워드:**`,
      `✅ 약속, 만남, 회의, 미팅, 모임, 식사, 점심, 저녁, 밥, 술, 회식`,
      `✅ 병원, 운동, 영화, 쇼핑, 여행, 공연, 콘서트`,
      `✅ 생일, 기념일, 파티, 결혼식, 장례식`,
      `✅ 수업, 강의, 세미나, 워크샵, 교육`,
      ``,
      `**일정 아님 (무시해야 할 것들):**`,
      `❌ "금요일 주니어B 과목 삭제" → 이건 다른 시스템의 텍스트 (일정 아님!)`,
      `❌ "수요일 공연반까지만" → 이건 다른 시스템의 텍스트 (일정 아님!)`,
      `❌ "토요일 KPOP 없애줘" → 이건 다른 시스템의 텍스트 (일정 아님!)`,
      `❌ "과목", "수업 삭제", "시간표" 같은 단어가 있으면 → 일정 아닐 가능성 높음!`,
      ``,
      `**판단 방법:**`,
      `1. "과목", "주니어", "레벨", "반", "시간표" 같은 단어 있으면 → **일정 아님 (오류 반환!)**`,
      `2. 약속/만남/식사 관련 명확한 단어 있으면 → 일정 맞음`,
      `3. 애매하면 → 사용자에게 확인 질문`,
      ``,
      `**일정이 아닐 때 응답:**`,
      `{"intent": "error", "response": "일정 관련 내용이 아닌 것 같아요. 😊\\n\\n일정을 추가하려면 '내일 저녁 6시 밥약속' 같은 형식으로 말씀해주세요!"}`,
      ``,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `📝 **두 번째: intent 판단 규칙 (일정 관련일 때만)**`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      ``,
      `**add_event (일정 추가):**`,
      `- "추가", "만들어", "생성", "넣어", "등록" 명시적 키워드`,
      `- "다음 내용으로 일정 추가: ..." → add_event`,
      `- 약속/만남/회의 등 일정 키워드만 있고 삭제/수정 없으면 → add_event`,
      ``,
      `**delete_event (일정 삭제):**`,
      `- "삭제", "제거", "없애", "지워", "취소" 명시적 키워드`,
      ``,
      `**edit_event (일정 수정):**`,
      `- "수정", "변경", "바꿔", "옮겨", "미뤄", "당겨" 명시적 키워드`,
      ``,
      `**기본값: 삭제/수정 키워드 없으면 → add_event**`,
      ``,
      `**정확한 날짜 계산 (오늘 기준):**`,
      `어제 = ${formatDate(addDays(now, -1))}`,
      `오늘 = ${formatDate(now)}`,
      `내일 = ${formatDate(addDays(now, 1))}`,
      `모레 = ${formatDate(addDays(now, 2))}`,
      `글피 = ${formatDate(addDays(now, 3))}`,
      ``,
      `절대 설명하지 마! JSON만 출력!`,
      ``,
      `**이번주 날짜:**`,
      `이번주 월요일 = ${formatDate(getWeekday(now, 1, 0))}`,
      `이번주 목요일 = ${formatDate(getWeekday(now, 4, 0))}`,
      ``,
      `**정확한 주차 계산:**`,
      `저저번주 = ${formatDate(startOfWeek(addWeeks(now, -2)))} ~ ${formatDate(endOfWeek(addWeeks(now, -2)))}`,
      `저번주 = ${formatDate(startOfWeek(addWeeks(now, -1)))} ~ ${formatDate(endOfWeek(addWeeks(now, -1)))}`,
      `이번주 = ${formatDate(startOfWeek(now))} ~ ${formatDate(endOfWeek(now))}`,
      `다음주 = ${formatDate(startOfWeek(addWeeks(now, 1)))} ~ ${formatDate(endOfWeek(addWeeks(now, 1)))}`,
      `다다음주 = ${formatDate(startOfWeek(addWeeks(now, 2)))} ~ ${formatDate(endOfWeek(addWeeks(now, 2)))}`,
      ``,
      `**요일별 정확한 날짜:**`,
      `저번주 목요일 = ${formatDate(getWeekday(now, 4, -1))}`,
      `이번주 목요일 = ${formatDate(getWeekday(now, 4, 0))}`,
      `다음주 목요일 = ${formatDate(getWeekday(now, 4, 1))}`,
      `다다음주 목요일 = ${formatDate(getWeekday(now, 4, 2))}`,
      ``,
      `**중요: 일정=약속=미팅=회의=모임 (동일 의미)**`,
      ``,
      `**중요: "추가", "만들어", "생성", "넣어", "등록" = add_event**`,
      `**중요: "삭제", "제거", "없애", "지워" = delete_event**`,
      `**중요: "수정", "변경", "바꿔", "옮겨", "미뤄", "당겨" = edit_event**`,
      ``,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `🧠 **스마트 일정 생성 - 사람처럼 생각하세요! (매우 중요!)**`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      ``,
      `**현재 한국 시간: ${now.getHours()}시 ${now.getMinutes()}분 (${formatDate(now, 'YYYY-MM-DD dddd')})**`,
      ``,
      `**📌 핵심 원칙: 일정 유형에 맞는 시간대를 선택하세요!**`,
      ``,
      `🌅 **아침 시간대 (06:00-10:00)**`,
      `   - 아침식사/조식: 07:00-08:00 (1시간)`,
      `   - 모닝커피: 08:00-08:30 (30분)`,
      `   - 아침운동/조깅: 06:00-07:30 (1.5시간)`,
      `   - 출근미팅: 08:30-09:30 (1시간)`,
      ``,
      `☀️ **오전 시간대 (10:00-12:00)**`,
      `   - 회의/미팅: 10:00-11:00 또는 11:00-12:00 (1시간)`,
      `   - 업무/작업: 10:00-12:00 (2시간)`,
      `   - 병원/검진: 10:00-12:00 (2시간)`,
      ``,
      `🍱 **점심 시간대 (12:00-14:00)**`,
      `   - 점심/런치: 12:00-13:00 또는 12:30-13:30 (1시간)`,
      `   - 점심약속: 12:00-13:30 (1.5시간)`,
      ``,
      `🌤️ **오후 시간대 (14:00-18:00)**`,
      `   - 회의/미팅: 14:00-15:00, 15:00-16:00, 16:00-17:00 (1시간)`,
      `   - 커피/티타임: 15:00-15:30 또는 16:00-16:30 (30분)`,
      `   - 공부/작업/프로젝트: 14:00-17:00 (2-3시간)`,
      `   - 쇼핑: 15:00-17:00 (2시간)`,
      ``,
      `🌆 **저녁 시간대 (18:00-21:00) ⭐가장 많이 사용⭐**`,
      `   - 저녁/저녁식사/밥약속: 18:00-20:00 또는 18:30-20:30 (2시간)`,
      `   - 저녁약속/식사약속: 18:00-20:00 (2시간)`,
      `   - 술약속/회식: 19:00-22:00 (3시간)`,
      `   - 저녁운동/헬스: 18:00-19:30 (1.5시간)`,
      `   - 저녁모임: 19:00-21:00 (2시간)`,
      ``,
      `🌃 **밤 시간대 (19:00-23:00)**`,
      `   - 영화: 19:00-21:30 또는 20:00-22:30 (2.5시간)`,
      `   - 공연/콘서트: 19:00-21:30 (2.5시간)`,
      `   - 친구만남/데이트: 19:00-22:00 (3시간)`,
      `   - 야식/치맥: 21:00-23:00 (2시간)`,
      ``,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `❌ **절대 하지 말아야 할 것들!**`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `❌ 저녁/밥약속을 오전에 잡지 마세요! (18:00-20:00가 정상)`,
      `❌ 점심을 저녁에 잡지 마세요! (12:00-13:00가 정상)`,
      `❌ 아침식사를 오후에 잡지 마세요! (07:00-08:00가 정상)`,
      `❌ 회의를 저녁/밤에 잡지 마세요! (10:00-17:00가 정상)`,
      `❌ 술약속을 아침/오전에 잡지 마세요! (19:00-22:00가 정상)`,
      ``,
      `✅ **올바른 예시:**`,
      `✅ "금요일 저녁 6시 밥약속" → 18:00-20:00 (저녁 시간대!)`,
      `✅ "내일 점심약속" → 12:00-13:00 (점심 시간대!)`,
      `✅ "오늘 저녁 술약속" → 19:00-22:00 (저녁-밤 시간대!)`,
      `✅ "오후 회의" → 14:00-15:00 (오후 시간대!)`,
      `✅ "커피 한잔" → 15:00-15:30 (오후 시간대!)`,
      ``,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `⏰ **시간 범위 지정 (매우 중요!):**`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `사용자가 시간 범위를 지정하면 정확히 그 시간을 사용하세요!`,
      ``,
      `✅ "오후 4시부터 6시까지" → 16:00-18:00`,
      `✅ "4~6시" → 16:00-18:00 (오후로 추정)`,
      `✅ "저녁 6시부터 8시" → 18:00-20:00`,
      `✅ "오전 10시-12시" → 10:00-12:00`,
      `✅ "2시간 약속" → 사용자 의도에 맞는 시간대 (예: 14:00-16:00)`,
      ``,
      `❌ "4시부터 6시"를 18:00-20:00로 만들지 마세요!`,
      `❌ 사용자가 지정한 시간을 무시하지 마세요!`,
      ``,
      `**시간 표현 이해:**`,
      `- "4시" 단독 = 오후 4시 (16:00)`,
      `- "오후 4시" = 16:00`,
      `- "저녁 6시" = 18:00`,
      `- "밤 9시" = 21:00`,
      `- "오전 9시" = 09:00`,
      ``,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `📋 **시간이 명시되지 않은 경우만 기본값 사용**`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `"밥약속" → 18:00-20:00 (2시간, 저녁)`,
      `"저녁약속" → 18:30-20:30 (2시간, 저녁)`,
      `"술약속" → 19:00-22:00 (3시간, 저녁-밤)`,
      `"점심약속" → 12:00-13:00 (1시간, 점심)`,
      `"커피" → 15:00-15:30 (30분, 오후)`,
      `"회의" → 14:00-15:00 (1시간, 오후)`,
      `"미팅" → 14:00-15:00 (1시간, 오후)`,
      `"운동" → 18:00-19:30 (1.5시간, 저녁)`,
      `"영화" → 19:00-21:30 (2.5시간, 밤)`,
      `"쇼핑" → 15:00-17:00 (2시간, 오후)`,
      `"병원" → 10:00-12:00 (2시간, 오전)`,
      ``,
      `**매우 중요: 정확한 날짜 계산!**`,
      `**현재 한국 시간: ${now.toString()}**`,
      `**오늘: ${formatDate(now, 'YYYY-MM-DD dddd')} (${formatDate(now)})**`,
      `**내일: ${formatDate(addDays(now, 1), 'YYYY-MM-DD dddd')} (${formatDate(addDays(now, 1))})**`,
      `**모레: ${formatDate(addDays(now, 2), 'YYYY-MM-DD dddd')} (${formatDate(addDays(now, 2))})**`,
      ``,
      `**중요: 모든 시간은 반드시 한국 시간(+09:00)으로 표기!**`,
      `- "내일"은 반드시 "${formatDate(addDays(now, 1))}" (절대 다른 날짜 안됨!)`,
      `- "오늘"은 반드시 "${formatDate(now)}" (절대 다른 날짜 안됨!)`,
      `- "모레"는 반드시 "${formatDate(addDays(now, 2))}" (절대 다른 날짜 안됨!)`,
      ``,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `📝 **정확한 예시 (반드시 따라하세요!)**`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      ``,
      `**시간 범위 지정 예시 (정확히 따라하세요!):**`,
      `"토요일 오후 4시부터 6시까지 약속" → {"intent": "add_event", "title": "약속", "startDateTime": "${formatDate(getWeekday(now, 6, 0))}T16:00:00+09:00", "endDateTime": "${formatDate(getWeekday(now, 6, 0))}T18:00:00+09:00", "response": "약속을 추가했어요!"}`,
      `"내일 2시~4시 회의" → {"intent": "add_event", "title": "회의", "startDateTime": "${formatDate(addDays(now, 1))}T14:00:00+09:00", "endDateTime": "${formatDate(addDays(now, 1))}T16:00:00+09:00", "response": "회의를 추가했어요!"}`,
      `"금요일 저녁 6-8시 저녁약속" → {"intent": "add_event", "title": "저녁약속", "startDateTime": "${formatDate(getWeekday(now, 5, 0))}T18:00:00+09:00", "endDateTime": "${formatDate(getWeekday(now, 5, 0))}T20:00:00+09:00", "response": "저녁약속을 추가했어요!"}`,
      `"오전 10시-12시 병원" → {"intent": "add_event", "title": "병원", "startDateTime": "${formatDate(now)}T10:00:00+09:00", "endDateTime": "${formatDate(now)}T12:00:00+09:00", "response": "병원 일정을 추가했어요!"}`,
      ``,
      `**일반 예시:**`,
      `"금요일 오후 6시 밥약속" → {"intent": "add_event", "title": "밥약속", "startDateTime": "${formatDate(getWeekday(now, 5, 0))}T18:00:00+09:00", "endDateTime": "${formatDate(getWeekday(now, 5, 0))}T20:00:00+09:00", "response": "밥약속을 추가했어요!"}`,
      `"내일 저녁약속" → {"intent": "add_event", "title": "저녁약속", "startDateTime": "${formatDate(addDays(now, 1))}T18:00:00+09:00", "endDateTime": "${formatDate(addDays(now, 1))}T20:00:00+09:00", "response": "저녁약속을 추가했어요!"}`,
      `"오늘 술약속" → {"intent": "add_event", "title": "술약속", "startDateTime": "${formatDate(now)}T19:00:00+09:00", "endDateTime": "${formatDate(now)}T22:00:00+09:00", "response": "술약속을 추가했어요!"}`,
      `"내일 점심약속" → {"intent": "add_event", "title": "점심약속", "startDateTime": "${formatDate(addDays(now, 1))}T12:00:00+09:00", "endDateTime": "${formatDate(addDays(now, 1))}T13:00:00+09:00", "response": "점심약속을 추가했어요!"}`,
      `"내일 회의" → {"intent": "add_event", "title": "회의", "startDateTime": "${formatDate(addDays(now, 1))}T14:00:00+09:00", "endDateTime": "${formatDate(addDays(now, 1))}T15:00:00+09:00", "response": "회의 일정을 추가했어요!"}`,
      `"오후 3시 커피" → {"intent": "add_event", "title": "커피", "startDateTime": "${formatDate(now)}T15:00:00+09:00", "endDateTime": "${formatDate(now)}T15:30:00+09:00", "response": "커피 일정을 추가했어요!"}`,
      `"다음주 월요일 영화" → {"intent": "add_event", "title": "영화", "startDateTime": "${formatDate(getWeekday(now, 1, 1))}T19:00:00+09:00", "endDateTime": "${formatDate(getWeekday(now, 1, 1))}T21:30:00+09:00", "response": "영화 일정을 추가했어요!"}`,
      `"이번주 금요일 운동" → {"intent": "add_event", "title": "운동", "startDateTime": "${formatDate(getWeekday(now, 5, 0))}T18:00:00+09:00", "endDateTime": "${formatDate(getWeekday(now, 5, 0))}T19:30:00+09:00", "response": "운동 일정을 추가했어요!"}`,
      ``,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `🔁 **반복/범위 일정 추가 (매우 중요!)**`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `"전부" 키워드가 있으면 범위 내 모든 날짜/요일에 일정 추가!`,
      ``,
      `**범위 패턴:**`,
      `1️⃣ "이번주 전부" = 오늘부터 이번주 일요일까지 매일`,
      `2️⃣ "다음주 전부" = 다음주 월요일부터 일요일까지 매일`,
      `3️⃣ "이번달 전부" = 이번달 1일부터 마지막날까지 매일 (지난 날짜 포함)`,
      `4️⃣ "이번주 월요일 전부" = 이번주의 월요일만`,
      `5️⃣ "이번달 월요일 전부" = 이번달의 모든 월요일 (1일부터 계산, 지난 날짜 포함)`,
      `6️⃣ "다음달 금요일 전부" = 다음달의 모든 금요일 (1일부터 마지막날까지)`,
      ``,
      `**❗❗❗ 매우 중요: 이번달/다음달 특정 요일 계산 방법 ❗❗❗**`,
      `**절대 규칙: "이번달 X요일 전부" = 해당 월의 1일부터 마지막날까지 모든 X요일!**`,
      ``,
      `**예시 (오늘: 2025-10-20 월요일):**`,
      `- "이번달 월요일 전부" → ["2025-10-06", "2025-10-13", "2025-10-20", "2025-10-27"] (10월의 모든 월요일 4개)`,
      `- "이번달 목요일 전부" → ["2025-10-02", "2025-10-09", "2025-10-16", "2025-10-23", "2025-10-30"] (10월의 모든 목요일 5개)`,
      `- "이번달 금요일 전부" → ["2025-10-03", "2025-10-10", "2025-10-17", "2025-10-24", "2025-10-31"] (10월의 모든 금요일 5개)`,
      ``,
      `**❌ 잘못된 예시:**`,
      `- "이번달 목요일 전부" → ["2025-10-23", "2025-10-30"] ❌ (지난 목요일 누락!)`,
      `- 남은 날짜만 계산하지 마세요! 반드시 1일부터 마지막날까지 전부!`,
      ``,
      `**반복 일정 JSON 형식:**`,
      `{`,
      `  "intent": "add_recurring_event",`,
      `  "title": "일정제목",`,
      `  "startTime": "18:00",  // 시간만 (HH:MM)`,
      `  "endTime": "20:00",    // 시간만 (HH:MM)`,
      `  "dates": ["2025-10-21", "2025-10-22", "2025-10-23"],  // 적용할 모든 날짜`,
      `  "response": "응답메시지"`,
      `}`,
      ``,
      `**반복 일정 예시:**`,
      `"이번주 전부 저녁약속" → {"intent": "add_recurring_event", "title": "저녁약속", "startTime": "18:00", "endTime": "20:00", "dates": ["${formatDate(now)}", "${formatDate(addDays(now, 1))}", "${formatDate(addDays(now, 2))}"], "response": "이번주 전체에 저녁약속을 추가했어요!"}`,
      `"다음주 전부 운동" → {"intent": "add_recurring_event", "title": "운동", "startTime": "18:00", "endTime": "19:30", "dates": ["${formatDate(startOfWeek(addWeeks(now, 1)))}", "${formatDate(addDays(startOfWeek(addWeeks(now, 1)), 1))}", ...], "response": "다음주 전체에 운동을 추가했어요!"}`,
      `"이번달 목요일 전부 회의" → {"intent": "add_recurring_event", "title": "회의", "startTime": "14:00", "endTime": "15:00", "dates": ["2025-10-02", "2025-10-09", "2025-10-16", "2025-10-23", "2025-10-30"], "response": "이번달 모든 목요일(5일)에 회의를 추가했어요!"}`,
      `"이번달 월요일 전부 운동" → {"intent": "add_recurring_event", "title": "운동", "startTime": "18:00", "endTime": "19:00", "dates": ["2025-10-06", "2025-10-13", "2025-10-20", "2025-10-27"], "response": "이번달 모든 월요일(4일)에 운동을 추가했어요!"}`,
      ``,
      `**중요:**`,
      `- "전부" 키워드 없으면 → intent: "add_event" (1회만)`,
      `- "전부" 키워드 있으면 → intent: "add_recurring_event" (범위 내 여러 날짜)`,
      `- dates 배열에는 YYYY-MM-DD 형식으로 모든 적용 날짜 포함`,
      `- startTime, endTime은 시간만 (HH:MM 형식)`,
      ``,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `🗑️ **일정 삭제 (매우 중요!)**`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      ``,
      `**1️⃣ 범위 삭제 (전체 삭제):**`,
      `"전체 삭제", "전부 삭제" 키워드가 있으면 범위 내 모든 일정 삭제!`,
      ``,
      `**범위 삭제 JSON 형식:**`,
      `{`,
      `  "intent": "delete_range",`,
      `  "startDate": "2025-10-01",  // 시작 날짜 (YYYY-MM-DD)`,
      `  "endDate": "2025-10-31",    // 종료 날짜 (YYYY-MM-DD)`,
      `  "response": "응답메시지"`,
      `}`,
      ``,
      `**범위 삭제 예시:**`,
      `"이번주 전체 삭제" → {"intent": "delete_range", "startDate": "${formatDate(startOfWeek(now))}", "endDate": "${formatDate(endOfWeek(now))}", "response": "이번주 모든 일정을 삭제했어요!"}`,
      `"이번달 전체 삭제" → {"intent": "delete_range", "startDate": "${formatDate(startOfMonth(now))}", "endDate": "${formatDate(endOfMonth(now))}", "response": "이번달 모든 일정을 삭제했어요!"}`,
      `"다음주 전부 삭제" → {"intent": "delete_range", "startDate": "${formatDate(startOfWeek(addWeeks(now, 1)))}", "endDate": "${formatDate(endOfWeek(addWeeks(now, 1)))}", "response": "다음주 모든 일정을 삭제했어요!"}`,
      `"10월 전체 삭제" → {"intent": "delete_range", "startDate": "2025-10-01", "endDate": "2025-10-31", "response": "10월 모든 일정을 삭제했어요!"}`,
      ``,
      `**2️⃣ 단일/특정 일정 삭제:**`,
      `특정 제목이나 날짜의 일정만 삭제`,
      ``,
      `**단일 삭제 JSON 형식:**`,
      `{`,
      `  "intent": "delete_event",`,
      `  "title": "일정제목",  // 삭제할 일정 제목`,
      `  "date": "2025-10-23",  // 날짜 (YYYY-MM-DD)`,
      `  "time": "16:00",      // 선택적 - 시간 (HH:MM)`,
      `  "response": "응답메시지"`,
      `}`,
      ``,
      `**단일 삭제 예시:**`,
      `"금요일 약속 삭제" → {"intent": "delete_event", "title": "약속", "date": "${formatDate(getWeekday(now, 5, 0))}", "response": "약속을 삭제했어요!"}`,
      `"내일 회의 삭제" → {"intent": "delete_event", "title": "회의", "date": "${formatDate(addDays(now, 1))}", "response": "회의를 삭제했어요!"}`,
      `"오후 4시 일정 삭제" → {"intent": "delete_event", "date": "${formatDate(now)}", "time": "16:00", "response": "오후 4시 일정을 삭제했어요!"}`,
      ``,
      `**중요:**`,
      `- "전체", "전부", "모든" 등의 키워드 → intent: "delete_range"`,
      `- 특정 제목/시간 지정 → intent: "delete_event"`,
      ``,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `✏️ **일정 수정 (매우 중요!)**`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      ``,
      `기존 일정의 시간, 제목, 날짜를 변경할 수 있습니다.`,
      ``,
      `**수정 JSON 형식:**`,
      `{`,
      `  "intent": "edit_event",`,
      `  "originalTitle": "원래 일정 제목",  // 찾을 일정 (필수!)`,
      `  "originalDate": "2025-10-23",  // 원래 날짜 (YYYY-MM-DD)`,
      `  "newTitle": "새 제목",  // 변경할 제목 (선택)`,
      `  "newDate": "2025-10-24",  // 변경할 날짜 (선택)`,
      `  "newStartTime": "18:00",  // 변경할 시작 시간 (HH:MM, 선택)`,
      `  "newEndTime": "20:00",  // 변경할 종료 시간 (HH:MM, 선택)`,
      `  "response": "응답메시지"`,
      `}`,
      ``,
      `**수정 예시:**`,
      `"금요일 회의 시간을 4시로 바꿔줘" → {"intent": "edit_event", "originalTitle": "회의", "originalDate": "${formatDate(getWeekday(now, 5, 0))}", "newStartTime": "16:00", "newEndTime": "17:00", "response": "회의 시간을 4시로 변경했어요!"}`,
      `"내일 밥약속을 저녁약속으로 수정해줘" → {"intent": "edit_event", "originalTitle": "밥약속", "originalDate": "${formatDate(addDays(now, 1))}", "newTitle": "저녁약속", "response": "밥약속을 저녁약속으로 변경했어요!"}`,
      `"목요일 운동을 금요일로 옮겨줘" → {"intent": "edit_event", "originalTitle": "운동", "originalDate": "${formatDate(getWeekday(now, 4, 0))}", "newDate": "${formatDate(getWeekday(now, 5, 0))}", "response": "운동을 금요일로 옮겼어요!"}`,
      `"오늘 회의 30분 미뤄줘" → {"intent": "edit_event", "originalTitle": "회의", "originalDate": "${formatDate(now)}", "newStartTime": "계산필요", "response": "회의를 30분 미뤘어요!"}`,
      ``,
      `**중요:**`,
      `- originalTitle과 originalDate로 기존 일정을 찾습니다`,
      `- 변경하고 싶은 필드만 포함하면 됩니다`,
      `- "미뤄", "당겨"는 시간 계산 후 newStartTime/newEndTime 설정`,
      ``,
      `**일정 충돌 시나리오:**`,
      `만약 시스템이 일정 충돌을 감지하면, 자동으로 대안 시간을 제시합니다.`,
      `당신은 JSON만 반환하면 됩니다. 충돌 감지는 시스템이 처리합니다.`,
      ``,
      `**기본 JSON 형식:**`,
      `{"intent": "add_event", "title": "일정", "startDateTime": "2025-09-08T16:00:00+09:00", "endDateTime": "2025-09-08T17:00:00+09:00", "response": "추가!"}`,
      ``,
      `**매우 중요:** 일정 관련이 아닌 단순 대화일 경우 → {"intent": "clarification", "response": "안녕하세요! 일정 관리를 도와드릴까요?"}`,
   ].join('\n');
};

/**
 * parseAIResponse
 * @description AI가 생성한 텍스트 응답에서 JSON 문자열을 추출하고 파싱합니다.
 */
export const parseAIResponse = text => {
   let jsonString = text.replace(/```json\n|\n```/g, '').trim();
   const jsonStart = jsonString.indexOf('{');
   const jsonEnd = jsonString.lastIndexOf('}');
   if (jsonStart !== -1 && jsonEnd !== -1) {
      jsonString = jsonString.substring(jsonStart, jsonEnd + 1);
   }
   jsonString = jsonString.replace(/\/\/.*$/gm, '').trim();

   const eventData = JSON.parse(jsonString);

   if (!eventData.title) eventData.title = '약속';
   if (!eventData.endDateTime && eventData.startDateTime) {
      const start = new Date(eventData.startDateTime);
      start.setHours(start.getHours() + 1);
      eventData.endDateTime = start.toISOString();
   }

   return eventData;
};

/**
 * checkScheduleConflict
 * @description 새 일정과 기존 일정 목록 간의 시간 충돌을 확인합니다.
 */
export const checkScheduleConflict = (newStartDateTime, newEndDateTime, existingEvents) => {
   const newStart = new Date(newStartDateTime);
   const newEnd = new Date(newEndDateTime);

   const conflicts = existingEvents.filter((event, idx) => {
      let eventStart, eventEnd;

      if (event.start && event.end) {
         eventStart = new Date(event.start.dateTime || event.start.date);
         eventEnd = new Date(event.end.dateTime || event.end.date);
      } else if (event.startTime && event.endTime) {
         eventStart = new Date(event.startTime);
         eventEnd = new Date(event.endTime);
      } else if (event.date && event.time) {
         const duration = event.duration || 60;
         eventStart = new Date(`${event.date}T${event.time}:00+09:00`);
         eventEnd = new Date(eventStart.getTime() + duration * 60 * 1000);
      } else {
         return false;
      }

      const hasConflict = newStart < eventEnd && newEnd > eventStart;
      return hasConflict;
   });

   return {
      hasConflict: conflicts.length > 0,
      conflicts
   };
};

/**
 * findAvailableTimeSlots
 * @description 특정 날짜에 주어진 기간만큼의 빈 시간 슬롯을 검색합니다.
 */
export const findAvailableTimeSlots = (targetDate, events, duration = 60, requestedTimeHour = null) => {
   const date = new Date(targetDate);
   const dateStr = formatDate(date);

   const dayEvents = events.filter(event => {
      let eventStart;
      if (event.start) {
         eventStart = new Date(event.start.dateTime || event.start.date);
      } else if (event.startTime) {
         eventStart = new Date(event.startTime);
      } else if (event.date && event.time) {
         eventStart = new Date(`${event.date}T${event.time}:00+09:00`);
      } else {
         return false;
      }
      return formatDate(eventStart) === dateStr;
   });

   dayEvents.sort((a, b) => {
      let aStart, bStart;
      if (a.start) {
         aStart = new Date(a.start.dateTime || a.start.date);
      } else if (a.startTime) {
         aStart = new Date(a.startTime);
      } else if (a.date && a.time) {
         aStart = new Date(`${a.date}T${a.time}:00+09:00`);
      }

      if (b.start) {
         bStart = new Date(b.start.dateTime || b.start.date);
      } else if (b.startTime) {
         bStart = new Date(b.startTime);
      } else if (b.date && b.time) {
         bStart = new Date(`${b.date}T${b.time}:00+09:00`);
      }

      return aStart - bStart;
   });

   const availableSlots = [];
   const workStart = 9;
   const workEnd = 22;
   const bufferMinutes = 0;

   let currentHour = workStart;

   for (const event of dayEvents) {
      let eventStart, eventEnd;
      if (event.start) {
         eventStart = new Date(event.start.dateTime || event.start.date);
         eventEnd = new Date(event.end.dateTime || event.end.date);
      } else if (event.startTime) {
         eventStart = new Date(event.startTime);
         eventEnd = new Date(event.endTime);
      } else if (event.date && event.time) {
         const duration = event.duration || 60;
         eventStart = new Date(`${event.date}T${event.time}:00+09:00`);
         eventEnd = new Date(eventStart.getTime() + duration * 60 * 1000);
      }
      const eventStartHour = eventStart.getHours() + eventStart.getMinutes() / 60;
      const eventEndHour = eventEnd.getHours() + eventEnd.getMinutes() / 60;

      const availableDuration = (eventStartHour - currentHour) * 60;

      if (availableDuration >= duration) {
         const slotEndHour = currentHour + (duration / 60);
         const slot = {
            start: `${Math.floor(currentHour).toString().padStart(2, '0')}:${Math.round((currentHour % 1) * 60).toString().padStart(2, '0')}`,
            end: `${Math.floor(slotEndHour).toString().padStart(2, '0')}:${Math.round((slotEndHour % 1) * 60).toString().padStart(2, '0')}`,
            date: dateStr,
            duration: duration,
            slotStartHour: currentHour
         };
         availableSlots.push(slot);
      }

      currentHour = eventEndHour + (bufferMinutes / 60);
   }

   const remainingDuration = (workEnd - currentHour) * 60;

   if (remainingDuration >= duration) {
      const slotEndHour = currentHour + (duration / 60);
      const slot = {
         start: `${Math.floor(currentHour).toString().padStart(2, '0')}:${Math.round((currentHour % 1) * 60).toString().padStart(2, '0')}`,
         end: `${Math.floor(slotEndHour).toString().padStart(2, '0')}:${Math.round((slotEndHour % 1) * 60).toString().padStart(2, '0')}`,
         date: dateStr,
         duration: duration,
         slotStartHour: currentHour
      };
      availableSlots.push(slot);
   }

   if (requestedTimeHour !== null) {
      availableSlots.sort((a, b) => {
         const distanceA = Math.abs(a.slotStartHour - requestedTimeHour);
         const distanceB = Math.abs(b.slotStartHour - requestedTimeHour);
         return distanceA - distanceB;
      });
   }

   return availableSlots;
};
